/**
 * Admin Users Page - Extends the Users component with admin-only
 * actions like permanent delete (all data + sites + containers)
 */
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { DeleteForever } from "@mui/icons-material";
import Users from "../Users";
import {
  adminPermanentDeleteUser,
  clearAdminError,
  clearAdminSuccess,
  fetchAdminPlans,
} from "../../redux/slices/adminSlice";
import { fetchAllUsers } from "../../redux/slices/userManagementSlice";

const AdminUsers = () => {
  const dispatch = useDispatch();
  const { actionLoading, error, successMessage, plans } = useSelector(
    (state) => state.admin,
  );
  const { user: currentUser } = useSelector((state) => state.user);

  const [permanentDeleteOpen, setPermanentDeleteOpen] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState("");
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [selfDeleteError, setSelfDeleteError] = useState("");

  useEffect(() => {
    dispatch(fetchAdminPlans());
  }, [dispatch]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => dispatch(clearAdminSuccess()), 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, dispatch]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => dispatch(clearAdminError()), 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  const handlePermanentDelete = async () => {
    if (!deleteUserId.trim() || deleteConfirmText !== "PERMANENT DELETE")
      return;

    // Prevent admin from deleting themselves
    if (
      deleteUserId.trim() === currentUser?.userId ||
      deleteUserId.trim() === currentUser?.id
    ) {
      setSelfDeleteError("You cannot permanently delete your own account.");
      return;
    }

    setSelfDeleteError("");
    await dispatch(adminPermanentDeleteUser(deleteUserId.trim()));
    setPermanentDeleteOpen(false);
    setDeleteUserId("");
    setDeleteConfirmText("");
    dispatch(fetchAllUsers({ page: 1, limit: 10 }));
  };

  return (
    <Box>
      {/* Admin-specific alerts */}
      {successMessage && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => dispatch(clearAdminSuccess())}
        >
          {successMessage}
        </Alert>
      )}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => dispatch(clearAdminError())}
        >
          {error}
        </Alert>
      )}

      {/* Regular Users page (includes table, filters, CRUD dialogs) */}
      <Users plans={plans} />

      {/* Floating Permanent Delete Button — placed after the Users table */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
        <Button
          variant="outlined"
          color="error"
          size="small"
          startIcon={<DeleteForever />}
          onClick={() => {
            setPermanentDeleteOpen(true);
            setSelfDeleteError("");
            setDeleteUserId("");
            setDeleteConfirmText("");
          }}
        >
          Permanent Delete User
        </Button>
      </Box>

      {/* Permanent Delete Dialog */}
      <Dialog
        open={permanentDeleteOpen}
        onClose={() => setPermanentDeleteOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600} color="error.main">
            Permanently Delete User
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This will <strong>permanently</strong> delete the user account, ALL
            their sites, containers, volumes, databases, backups, and all
            associated data. This action <strong>CANNOT be undone</strong>.
          </Alert>
          {selfDeleteError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {selfDeleteError}
            </Alert>
          )}
          <TextField
            fullWidth
            label="User ID"
            placeholder="Enter the user ID to permanently delete"
            value={deleteUserId}
            onChange={(e) => {
              setDeleteUserId(e.target.value);
              setSelfDeleteError("");
            }}
            size="small"
            helperText="Enter the exact user ID (cuid) of the account to destroy"
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label='Type "PERMANENT DELETE" to confirm'
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            size="small"
            error={
              deleteConfirmText.length > 0 &&
              deleteConfirmText !== "PERMANENT DELETE"
            }
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setPermanentDeleteOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handlePermanentDelete}
            disabled={
              actionLoading ||
              !deleteUserId.trim() ||
              deleteConfirmText !== "PERMANENT DELETE"
            }
            startIcon={<DeleteForever />}
          >
            {actionLoading ? "Deleting..." : "Permanently Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminUsers;
